import { useId } from "react";

import { classes } from "./classes.js";

/**
 * `F-0c`: **« le veilleur »**, Hakili's face (D3 P-3, ruled in for the demo; the trademark check is booked before
 * public use). An original hippopotamus bust, drawn from `docs/design/bank-v2/tools/mascot-veilleur.mjs`.
 *
 * ## Five states, each readable WITHOUT an icon
 *
 * `idle · reading · done · flagging · failed`. The state is carried by the face itself: the eyes (lids, size, a
 * happy arc), the brows, the mouth (one shape per state), the ears and the tilt of the head. The theme
 * (`bank-theme.css`) switches them by `data-state`; the corner badge and the word (`StateBadge`) are the second and
 * third signals and are never the only ones.
 *
 * ## Motion
 *
 * Every animation lives inside `@media (prefers-reduced-motion: no-preference)` in the theme, so a person who asked
 * for less sees the still face of the state, and the theme's reduced-motion rule also stops its transitions.
 *
 * The face is named by the caller (`label`, from the catalogue): this package holds no French. Two mascots on one
 * page do not share gradient or clip ids.
 */
export const MASCOT_STATES = ["idle", "reading", "done", "flagging", "failed"] as const;
export type MascotState = (typeof MASCOT_STATES)[number];

export interface HakiliMascotProps {
  readonly state: MascotState;
  /** The face's accessible name, from the catalogue. */
  readonly label: string;
  readonly className?: string;
}

interface EyeProps {
  readonly uid: string;
  readonly side: "l" | "r";
  readonly x: string;
}

function Eye({ uid, side, x }: EyeProps): React.JSX.Element {
  return (
    <>
      <g className={`eye ${side}`} transform={`translate(${x} 88)`}>
        <g className="ball">
          <ellipse rx="11.5" ry="13" fill="var(--v-eye)" />
          <g className="pup">
            <circle cx="3.6" cy="-4.4" r="4" fill="#fff" />
            <circle cx="-3.6" cy="4.6" r="1.7" fill="#fff" opacity=".9" />
          </g>
        </g>
        <clipPath id={`${uid}-c${side}`}>
          <ellipse rx="12" ry="13.5" />
        </clipPath>
        <g clipPath={`url(#${uid}-c${side})`}>
          <rect className="lid" x="-14" y="-16" width="28" height="32" fill={`url(#${uid}-skin)`} />
        </g>
        <path
          className="lidline"
          d="M-11 0 Q0 5 11 0"
          fill="none"
          stroke="var(--v-line)"
          strokeWidth="3.2"
          strokeLinecap="round"
          opacity="0"
        />
        <path
          className="happy"
          d="M-11 4 Q0 -11 11 4"
          fill="none"
          stroke="var(--v-eye)"
          strokeWidth="4.4"
          strokeLinecap="round"
          opacity="0"
        />
      </g>
      <g className={`brow ${side}`} transform={`translate(${x} 63)`}>
        <path
          d="M-13 0 Q0 -7 13 0"
          fill="none"
          stroke="var(--v-brow)"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </g>
    </>
  );
}

export function HakiliMascot({ state, label, className }: HakiliMascotProps): React.JSX.Element {
  // `useId` yields « :r1: », which is a legal id but not a comfortable url(#…) reference.
  const uid = `v${useId().replace(/:/gu, "")}`;
  return (
    <svg
      data-kit="mascot"
      className={classes("hippo hippo-veilleur", className)}
      data-state={state}
      viewBox="0 0 240 240"
      role="img"
      aria-label={label}
    >
      <defs>
        <radialGradient id={`${uid}-skin`} gradientUnits="userSpaceOnUse" cx="112" cy="70" r="150">
          <stop offset="0" stopColor="var(--v-skin-hi)" />
          <stop offset=".55" stopColor="var(--v-skin)" />
          <stop offset="1" stopColor="var(--v-skin-lo)" />
        </radialGradient>
        <radialGradient id={`${uid}-muz`} gradientUnits="userSpaceOnUse" cx="120" cy="122" r="70">
          <stop offset="0" stopColor="var(--v-muz-hi)" />
          <stop offset="1" stopColor="var(--v-muz)" />
        </radialGradient>
        <radialGradient id={`${uid}-halo`}>
          <stop offset="0" stopColor="var(--v-halo)" stopOpacity=".55" />
          <stop offset="1" stopColor="var(--v-halo)" stopOpacity="0" />
        </radialGradient>
        <filter id={`${uid}-felt`} x="-5%" y="-5%" width="110%" height="110%">
          <feTurbulence type="fractalNoise" baseFrequency="1.3" numOctaves="2" seed="4" result="n" />
          <feColorMatrix in="n" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 .35 -.1" result="s" />
          <feComposite in="s" in2="SourceAlpha" operator="in" result="g" />
          <feMerge>
            <feMergeNode in="SourceGraphic" />
            <feMergeNode in="g" />
          </feMerge>
        </filter>
        <filter id={`${uid}-soft`}>
          <feGaussianBlur stdDeviation="3.2" />
        </filter>
      </defs>
      <circle className="halo" cx="120" cy="112" r="118" fill={`url(#${uid}-halo)`} />
      <g className="all">
        <g className="shoulders" filter={`url(#${uid}-felt)`}>
          <path d="M26 244 C 26 202 66 184 120 184 C 174 184 214 202 214 244 Z" fill={`url(#${uid}-skin)`} />
          <path
            d="M92 186 Q 120 200 148 186"
            fill="none"
            stroke="var(--v-skin-lo)"
            strokeWidth="3"
            strokeLinecap="round"
            opacity=".45"
          />
        </g>
        <g className="head">
          <g className="ear l" transform="translate(56 56)">
            <g className="er">
              <circle r="16" fill={`url(#${uid}-skin)`} filter={`url(#${uid}-felt)`} />
              <ellipse cy="2" rx="7.5" ry="9" fill="var(--v-blush)" opacity=".75" />
            </g>
          </g>
          <g className="ear r" transform="translate(184 56)">
            <g className="er">
              <circle r="16" fill={`url(#${uid}-skin)`} filter={`url(#${uid}-felt)`} />
              <ellipse cy="2" rx="7.5" ry="9" fill="var(--v-blush)" opacity=".75" />
            </g>
          </g>
          <g filter={`url(#${uid}-felt)`}>
            <path
              d="M46 98 C 46 58 78 38 120 38 C 162 38 194 58 194 98 C 194 116 214 126 214 152 C 214 182 178 194 120 194 C 62 194 26 182 26 152 C 26 126 46 116 46 98 Z"
              fill={`url(#${uid}-skin)`}
            />
            <ellipse cx="80" cy="86" rx="19" ry="17" fill={`url(#${uid}-skin)`} />
            <ellipse cx="160" cy="86" rx="19" ry="17" fill={`url(#${uid}-skin)`} />
          </g>
          <path
            d="M62 56 C 78 44 98 40 120 40"
            fill="none"
            stroke="#fff"
            strokeOpacity=".5"
            strokeWidth="5"
            strokeLinecap="round"
            filter={`url(#${uid}-soft)`}
          />
          <g filter={`url(#${uid}-felt)`}>
            <path
              d="M36 152 C 36 128 64 118 120 118 C 176 118 204 128 204 152 C 204 178 172 188 120 188 C 68 188 36 178 36 152 Z"
              fill={`url(#${uid}-muz)`}
            />
          </g>
          <ellipse
            className="nostril"
            cx="92"
            cy="132"
            rx="9.5"
            ry="6"
            transform="rotate(-12 92 132)"
            fill="var(--v-nostril)"
          />
          <ellipse
            className="nostril"
            cx="148"
            cy="132"
            rx="9.5"
            ry="6"
            transform="rotate(12 148 132)"
            fill="var(--v-nostril)"
          />
          <ellipse
            className="cheek"
            cx="48"
            cy="150"
            rx="17"
            ry="10"
            fill="var(--v-cheek)"
            opacity=".55"
            filter={`url(#${uid}-soft)`}
          />
          <ellipse
            className="cheek"
            cx="192"
            cy="150"
            rx="17"
            ry="10"
            fill="var(--v-cheek)"
            opacity=".55"
            filter={`url(#${uid}-soft)`}
          />

          <Eye uid={uid} side="l" x="80" />
          <Eye uid={uid} side="r" x="160" />
          <g
            className="mouth"
            fill="none"
            stroke="var(--v-line)"
            strokeWidth="3.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path className="m-idle" d="M106 160 Q120 170 134 160" />
            <ellipse className="m-read" cx="120" cy="164" rx="5" ry="4.2" fill="var(--v-mouth)" />
            <path className="m-done" d="M98 156 Q120 182 142 156" fill="var(--v-mouth)" />
            <path className="m-flag" d="M104 164 Q112 157 120 164 Q128 171 136 164" />
            <path className="m-fail" d="M106 170 Q120 156 134 170" />
          </g>
        </g>
      </g>
    </svg>
  );
}
